// src/services/GuestPlusSessionManager.js
import { 
    hasSpecialAccess, 
    clearSpecialAccess, 
    getSpecialAccessUserId, 
    getSpecialAccessDisplayName,
    getSpecialAccessSessionId,
    updateSpecialAccessDisplayName 
} from './specialAccessService.js';

import { db, firebase } from '../firebaseConfig.js';

/**
 * Manages Guest+ session state, tracking, and transitions
 */
class GuestPlusSessionManager {
    constructor() {
        this.isInitialized = false;
        this.sessionData = {};
        this.heartbeatInterval = null;
        this.pingInterval = 5 * 60 * 1000;
        this.heartbeatPermissionDenied = false; // <-- ADD THIS LINE
    }

    /**
     * Initialize the Guest+ session manager
     */
    init() {
        if (this.isInitialized) return;

        // Check if we have special access
        if (!hasSpecialAccess()) {
            return; // No special access, nothing to initialize
        }

        const sessionId = getSpecialAccessSessionId();
        if (!sessionId) {
            console.warn("Guest+ session without session ID, cannot initialize tracking");
            return;
        }

        // Register event listeners for session state
        this.registerEventListeners();

        try {
            // Start session heartbeat to keep session alive in database
            this.startHeartbeat(sessionId);
        } catch (error) {
            console.warn("Failed to start session heartbeat due to permissions issue. Session will still work locally.");
        }
        // Get updated display name with latest time remaining
        updateSpecialAccessDisplayName();
        
        // Mark as initialized
        this.isInitialized = true;
        console.log("GuestPlusSessionManager initialized with session ID:", sessionId);
    }

    /**
     * Register event listeners for session state changes
     */
    registerEventListeners() {
        // Listen for session expiry
        window.addEventListener('special-access-expired', this.handleSessionEnd.bind(this));
        
        // Listen for session clearing
        window.addEventListener('special-access-cleared', this.handleSessionEnd.bind(this));
        
        // Listen for page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                // Update session data if page becomes visible
                this.updateSessionData();
            }
        });
        
        // Listen for page unload
        window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
    }

    /**
     * Start session heartbeat to keep the session alive in the database
     * @param {string} sessionId - The session ID to track
     */
   // Also update the sendHeartbeat method:
  // --- REVISE sendHeartbeat ---
sendHeartbeat(sessionId) {
    if (!hasSpecialAccess()) { this.stopHeartbeat(); return; }
    if (this.heartbeatPermissionDenied) { return; } // Skip if already denied

    try {
        db.collection('accessCodeSessions')
            .where('sessionId', '==', sessionId)
            .limit(1)
            .get()
            .then(snapshot => {
                if (!snapshot.empty) {
                    snapshot.forEach(doc => {
                        doc.ref.update({ // Attempt update
                            lastSeen: firebase.firestore.FieldValue.serverTimestamp(),
                            isActive: true
                        }).catch(err => { // Catch update error
                            if (err.code === 'permission-denied' || err.message.includes('permission')) {
                                console.warn("Guest+ Heartbeat update failed (permission denied). Disabling heartbeats.");
                                this.heartbeatPermissionDenied = true;
                                this.stopHeartbeat();
                            } else { console.warn("Failed to update session heartbeat:", err); }
                        });
                    });
                } else {
                     console.warn(`Guest+ Heartbeat: Session document for ${sessionId} not found.`);
                     // Optionally try to create it again, but this might indicate an issue.
                }
            })
            .catch(err => { // Catch get error
                 if (err.code === 'permission-denied' || err.message.includes('permission')) {
                     console.warn("Guest+ Heartbeat get failed (permission denied). Disabling heartbeats.");
                     this.heartbeatPermissionDenied = true;
                     this.stopHeartbeat();
                 } else { console.warn("Failed to query session heartbeat:", err); }
            });
    } catch (e) { console.warn("Error sending session heartbeat:", e); }
}
    /**
     * Update session data with latest information
     */
   // --- REVISE updateSessionData ---
updateSessionData() {
    if (!hasSpecialAccess()) return;
    const sessionId = getSpecialAccessSessionId();
    if (!sessionId) return;
    if (this.heartbeatPermissionDenied) { return; } // Use same flag

    const displayName = updateSpecialAccessDisplayName(); // Get latest name

    try {
        db.collection('accessCodeSessions')
            .where('sessionId', '==', sessionId)
            .limit(1)
            .get()
            .then(snapshot => {
                if (!snapshot.empty) {
                    snapshot.forEach(doc => {
                        doc.ref.update({ // Attempt update
                            displayName: displayName,
                            lastSeen: firebase.firestore.FieldValue.serverTimestamp()
                        }).catch(err => { // Catch update error
                            if (err.code === 'permission-denied' || err.message.includes('permission')) {
                                console.warn("Guest+ Session data update failed (permission denied). Disabling updates.");
                                this.heartbeatPermissionDenied = true; // Set flag
                            } else { console.warn("Failed to update session data:", err); }
                        });
                    });
                } else {
                    console.warn(`Guest+ UpdateData: Session document for ${sessionId} not found.`);
                }
            })
             .catch(err => { // Catch get error
                 if (err.code === 'permission-denied' || err.message.includes('permission')) {
                     console.warn("Guest+ Session data get failed (permission denied). Disabling updates.");
                     this.heartbeatPermissionDenied = true;
                 } else { console.warn("Failed to query session data:", err); }
             });
    } catch (e) { console.warn("Error updating session data:", e); }
}
    /**
     * Stop the heartbeat interval
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Handle session end events
     */
    handleSessionEnd() {
        // Stop heartbeat
        this.stopHeartbeat();
        
        // Mark session as ended in Firebase
        const sessionId = getSpecialAccessSessionId();
        if (sessionId) {
            try {
                db.collection('accessCodeSessions')
                    .where('sessionId', '==', sessionId)
                    .limit(1)
                    .get()
                    .then(snapshot => {
                        if (!snapshot.empty) {
                            snapshot.forEach(doc => {
                                doc.ref.update({
                                    isActive: false,
                                    endedAt: firebase.firestore.FieldValue.serverTimestamp(),
                                    endReason: 'session_end'
                                });
                            });
                        }
                    })
                    .catch(err => console.warn("Failed to update session end:", err));
            } catch (e) {
                console.warn("Error updating session end:", e);
            }
        }
        
        this.isInitialized = false;
    }

    /**
     * Handle page unload to mark session as potentially inactive
     */
    handlePageUnload() {
        const sessionId = getSpecialAccessSessionId();
        if (sessionId) {
            try {
                // Use sendBeacon for best chance of sending data before page unloads
                const data = JSON.stringify({
                    sessionId: sessionId,
                    action: 'page_unload',
                    timestamp: Date.now()
                });
                
                if (navigator.sendBeacon) {
                    navigator.sendBeacon('/api/track-session-unload', data);
                }
                
                // Fallback - attempt a synchronous request if sendBeacon not available
                // This will still often fail on page close
                if (!navigator.sendBeacon) {
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', '/api/track-session-unload', false); // Synchronous
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    xhr.send(data);
                }
            } catch (e) {
                // We expect this might fail during page unload
                console.warn("Error in page unload handler:", e);
            }
        }
    }
}

// Export singleton instance
const guestPlusSessionManager = new GuestPlusSessionManager();

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            guestPlusSessionManager.init();
        });
    } else {
        guestPlusSessionManager.init();
    }
    
    // Re-initialize on special access granted
    window.addEventListener('special-access-granted', () => {
        guestPlusSessionManager.init();
    });
}

export default guestPlusSessionManager;